import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AutoLoginPartialRoutesGuard } from 'angular-auth-oidc-client';

import { UnitDetailsComponent } from '@portfolio/components/unit-details/unit-details.component';
import { AppRoutes } from '@shared/constants/app-routes.const';
import { DashboardViewType } from '@shared/enums/dashboard-view-type';
import { EIcon } from '@shared/enums/icon.enum';
import { ManagementRoute } from '@shared/enums/management-route.enum';
import { PermissionLevelType } from '@shared/enums/permission-level.enum';
import { RouteData } from '@shared/enums/route-data';
import { RoutePath } from '@shared/enums/route-path.enum';
import { AuthGuard } from '@shared/guards/auth.guard';
import { HomeRedirectGuard } from '@shared/guards/home-redirect.guard';
import { PermissionLevelGuardFactory } from '@shared/guards/permission-level.guard';
import { ResidentGuard } from '@shared/guards/resident.guard';
import { ViewCloseGuard } from '@shared/guards/view-close.guard';

import { ApplicationMenuComponent } from './application-menu.component';

const routes: Routes = [
  {
    path: '',
    component: ApplicationMenuComponent,
    canActivate: [ResidentGuard],
    children: [
      {
        path: '',
        redirectTo: AppRoutes.HOME,
        pathMatch: 'full',
      },
      {
        path: RoutePath.HOME,
        canActivate: [AutoLoginPartialRoutesGuard, AuthGuard, HomeRedirectGuard],
        loadChildren: () =>
          import('@main-application/home-dashboard/home-dashboard.module').then(m => m.HomeDashboardModule),
      },
      {
        path: RoutePath.ROLES,
        canActivate: [AutoLoginPartialRoutesGuard, AuthGuard],
        data: { [RouteData.BREADCRUMB]: 'Home', [RouteData.BREADCRUMB_ICON]: EIcon.HOME },
        loadChildren: () =>
          import('@main-application/dashboard-roles/dashboard-roles.module').then(m => m.DashboardRolesModule),
      },
      {
        path: RoutePath.TURNOVERS,
        canActivate: [AutoLoginPartialRoutesGuard, AuthGuard],
        loadChildren: () => import('@main-application/turnovers/turnovers.module').then(m => m.TurnoversModule),
      },
      {
        path: RoutePath.INSPECTIONS,
        canActivate: [AutoLoginPartialRoutesGuard, AuthGuard],
        loadChildren: () => import('@main-application/inspections/inspections.module').then(m => m.InspectionsModule),
      },
      {
        path: RoutePath.Maintenance,
        canActivate: [AutoLoginPartialRoutesGuard, AuthGuard],
        loadChildren: () => import('@main-application/maintenance/maintenance.module').then(m => m.MaintenanceModule),
      },
      {
        path: RoutePath.CALENDAR,
        canActivate: [AutoLoginPartialRoutesGuard, AuthGuard],
        loadChildren: () => import('@main-application/calendar/calendar.module').then(m => m.CalendarModule),
      },
      {
        path: RoutePath.BOARDS,
        canActivate: [AutoLoginPartialRoutesGuard, AuthGuard],
        data: { [RouteData.BREADCRUMB]: 'Boards' },
        loadChildren: () => import('@main-application/boards/boards.module').then(m => m.BoardsModule),
      },
      {
        path: `${RoutePath.BUILDIUM}/tokenExchange`,
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('@main-application/buildium/token-exchange/token-exchange.component').then(
            m => m.TokenExchangeComponent
          ),
      },
      {
        path: RoutePath.REPORTS,
        canActivate: [AutoLoginPartialRoutesGuard, AuthGuard],
        data: {
          [RouteData.BREADCRUMB]: 'Reports',
          [RouteData.BREADCRUMB_ICON]: EIcon.CHART_LINE,
        },
        loadChildren: () =>
          import('@main-application/dashboard-reports/dashboard-reports.module').then(m => m.DashboardReportsModule),
      },
      {
        path: RoutePath.TICKETS,
        canActivate: [
          AutoLoginPartialRoutesGuard,
          AuthGuard,
          PermissionLevelGuardFactory(PermissionLevelType.Tickets_Read),
        ],
        loadChildren: () => import('@main-application/tickets/tickets.module').then(m => m.TicketsModule),
      },
      {
        path: ManagementRoute.SETTINGS,
        canActivate: [AutoLoginPartialRoutesGuard, AuthGuard],
        canDeactivate: [ViewCloseGuard],
        data: {
          [RouteData.BREADCRUMB]: 'Settings',
          [RouteData.BREADCRUMB_ICON]: EIcon.SHIELD_USER,
        },
        loadChildren: () => import('@main-application/management/management.module').then(m => m.ManagementModule),
      },
      {
        path: `${RoutePath.UNIT_DETAILS}/:unitId`,
        component: UnitDetailsComponent,
        canActivate: [AutoLoginPartialRoutesGuard, AuthGuard],
        canDeactivate: [ViewCloseGuard],
        data: {
          [RouteData.BREADCRUMB]: 'Unit details',
          [RouteData.DASHBOARD_VIEW_TYPE]: DashboardViewType.NONE,
        },
      },
      {
        path: RoutePath.NO_PERMISSION,
        canActivate: [AutoLoginPartialRoutesGuard],
        data: {
          [RouteData.BREADCRUMB]: 'No permission',
          [RouteData.DASHBOARD_VIEW_TYPE]: DashboardViewType.NONE,
        },
        loadComponent: () =>
          import('@main-application/no-permission/no-permission.component').then(m => m.NoPermissionComponent),
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class ApplicationMenuRoutingModule {}
